import type { BaseOptionType } from "antd/lib/select"
import { useEffect, useMemo, useState } from "react"

import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import {
  getReportShareCandidates,
  getReportShareRoleCandidates
} from "../api/services.ts"
import {
  sharedRoleIds,
  sharedUserIds,
  type ShareEditorEntry,
  type ShareEditorKind
} from "../helpers/reportSharing.ts"
import type {
  ReportShareCandidate,
  ReportShareRoleCandidate
} from "../types/collab.types.ts"
import type { ReportOwner } from "../types/types.ts"

export interface ShareUserOption extends BaseOptionType {
  value: string
  label: string
  email: string
}

export interface UseReportShareUsers {
  options: ShareUserOption[]
  loading: boolean
}

export interface ShareTargetOption extends BaseOptionType {
  value: string
  label: string
  kind: ShareEditorKind
  targetId: number | string
  email: string
}

export interface UseReportShareTargets {
  options: ShareTargetOption[]
  loading: boolean
}

export const filterShareUserOption = (
  input: string,
  option?: BaseOptionType
): boolean => {
  const needle = input.trim().toLowerCase()
  if (!needle) return true
  const label = String(option?.label ?? "").toLowerCase()
  const email = String(option?.email ?? "").toLowerCase()
  return label.includes(needle) || email.includes(needle)
}

export const filterShareTargetOption = filterShareUserOption

export const useShareCandidateOptions = (
  reportId: string
): UseReportShareUsers => {
  const [candidates, setCandidates] = useState<ReportShareCandidate[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setCandidates([])
    void (async () => {
      const response = await getReportShareCandidates(reportId)
      if (cancelled) return
      if (response.ok && response.data) {
        setCandidates(response.data)
      }
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [reportId])

  const options = useMemo(
    () =>
      candidates.map<ShareUserOption>((candidate) => ({
        value: String(candidate.id),
        label: getEntityName(candidate),
        email: candidate.email ?? ""
      })),
    [candidates]
  )

  return { options, loading }
}

export const useReportShareTargets = (
  reportId: string,
  owner: ReportOwner,
  entries: ShareEditorEntry[]
): UseReportShareTargets => {
  const { options: allUserOptions, loading: usersLoading } =
    useShareCandidateOptions(reportId)
  const [roleCandidates, setRoleCandidates] = useState<
    ReportShareRoleCandidate[]
  >([])
  const [rolesLoading, setRolesLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setRolesLoading(true)
    setRoleCandidates([])
    void (async () => {
      const response = await getReportShareRoleCandidates(reportId)
      if (cancelled) return
      setRoleCandidates(response.ok && response.data ? response.data : [])
      setRolesLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [reportId])

  const options = useMemo(() => {
    const takenUsers = sharedUserIds(entries)
    const takenRoles = sharedRoleIds(entries)
    const ownerId = String(owner.id)
    const users = allUserOptions
      .filter(
        (option) =>
          option.value !== ownerId && !takenUsers.has(option.value)
      )
      .map<ShareTargetOption>((option) => ({
        ...option,
        value: `user:${option.value}`,
        kind: "user",
        targetId: option.value
      }))
    const roles = roleCandidates
      .filter((role) => !takenRoles.has(String(role.id)))
      .map<ShareTargetOption>((role) => ({
        value: `role:${role.id}`,
        label: role.name,
        kind: "role",
        targetId: role.id,
        email: ""
      }))
    return [...users, ...roles]
  }, [allUserOptions, entries, owner.id, roleCandidates])

  return { options, loading: usersLoading || rolesLoading }
}
